import React from 'react';

import { bemBlock } from '../../modules/bem';
import { type GlyphName, Icon } from '../icon/Icon';
import { Layout } from '../layout/Layout';
import { Text } from '../text/Text';

import { getDataRole } from './Panel.helpers';
import { type DataRoleProps, type PanelIntent } from './Panel.types';
import { PanelStatusBadge } from './PanelStatusBadge';

import './PanelHeader.less';

const block = bemBlock('n-Panel');

export type PanelHeaderProps = Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> &
  DataRoleProps & {
    badge?: React.ReactNode;
    badgeIntent?: PanelIntent | 'disabled';
    description?: React.ReactNode;
    icon?: GlyphName;
    intent?: PanelIntent;
    title?: React.ReactNode;
  };

export const PanelHeader = React.forwardRef<HTMLDivElement, PanelHeaderProps>(function PanelHeader(
  {
    'aria-live': ariaLive,
    badge,
    badgeIntent,
    children,
    className,
    dataRole,
    description,
    icon,
    intent = 'info',
    title,
    'data-role': dataRoleAttribute,
    ...passProps
  },
  ref,
) {
  return (
    <Layout.Element
      {...passProps}
      elementRef={ref}
      aria-live={ariaLive}
      className={block({
        element: 'header',
        modifiers: { intent, 'with-icon': icon !== undefined },
        extra: className,
      })}
      data-role={getDataRole(dataRole, dataRoleAttribute)}
    >
      {icon !== undefined && (
        <span className={block({ element: 'header-icon', modifiers: { intent } })}>
          <Icon glyph={icon} />
        </span>
      )}
      <Layout.Element className={block('header-content')}>
        {children ?? (
          <>
            <Layout.Row alignItems="center" spacedChildren="sm" wrap="wrap">
              {title !== undefined && (
                <Text size="md" fontWeight="semibold">
                  {title}
                </Text>
              )}
              {badge !== undefined && (
                <PanelStatusBadge intent={badgeIntent ?? intent}>{badge}</PanelStatusBadge>
              )}
            </Layout.Row>
            {description !== undefined && (
              <Text color="text-secondary" size="sm">
                {description}
              </Text>
            )}
          </>
        )}
      </Layout.Element>
    </Layout.Element>
  );
});
